import WordStatus from './WatchlistTableComponents/WordStatus';
import AddRemoveIcon from './WatchlistTableComponents/AddRemoveIcon';
import AggregatedSentimentRenderer from './WatchlistTableComponents/AggregatedSentimentRenderer';
import SentimentChangeRenderer from './WatchlistTableComponents/SentimentChangeRenderer';
import WordCountChangeRenderer from './WatchlistTableComponents/WordCountChangeRenderer';
import WordCountChangePercentageRenderer from './WatchlistTableComponents/WordCountChangePercentageRenderer';
import TickerLogo from './WatchlistTableComponents/TickerLogo';
import CountryCodeRenderer from './WatchlistTableComponents/CountryCodeRenderer';
import TweetsIcon from './WatchlistTableComponents/tweet';
import ScoreWordRenderer from './WatchlistTableComponents/ScoreWordRenderer';
import SocialCheckboxRenderer from './WatchlistTableComponents/SocialCheckboxRenderer';
import WatchlistService from './WatchlistService';
import isEmpty from 'lodash/isEmpty';
import forEach from 'lodash/forEach';
import debounce from 'lodash/debounce';
import round from 'lodash/round';
import { storeColumnsState, getColumnState, getFilteringState, getFilterCountriesArray } from './WatchlistHelpers';

import { getUserDateFormat } from '../../utils/helpers';
import moment from 'moment';
const parser = new DOMParser();

const changedStyles = {
  lowest: { backgroundColor: '#bf2828', color: '#ffffff', width: 80, textTransform: 'capitalize' },
  low: { backgroundColor: '#f50101', color: '#ffffff', width: 80, textTransform: 'capitalize' },
  median: { backgroundColor: '#263055', color: '#ffffff', width: 80, textTransform: 'capitalize' },
  high: { backgroundColor: '#0de63f', color: '#ffffff', width: 80, textTransform: 'capitalize' },
  highest: { backgroundColor: '#1bc943', color: '#ffffff', width: 80, textTransform: 'capitalize' },
};
export { changedStyles };

export const numberWordComparator = (value1, value2, nodeA, nodeB, isInverted) => {
  let value1ComparableValue = value1 === null ? (!isInverted ? +1 * Infinity : -1 * Infinity) : value1.number;
  let value2ComparableValue = value2 === null ? (!isInverted ? +1 * Infinity : -1 * Infinity) : value2.number;
  return value1ComparableValue - value2ComparableValue;
};

export const formatExportValue = params => {
  const colId = params?.column?.colDef?.colId;
  const value = params?.value;
  if (colId) {
    if (colId === 'actions' || colId === 'tweetsFlag') {
      if (value) {
        return 'Yes';
      } else {
        return 'No';
      }
    } else if (colId === 'mktcap_' || colId === 'adv') {
      return currencyFormater(value, 0, 'USD');
    } else if (colId === 'mktcap') {
      return currencyFormater(value, 0, params.node.data.currCode);
    } else if (['sentimentWord', 'sentimentChangeWord', 'wordCountChangePercentWord'].includes(colId)) {
      return value?.word || '';
    } else if (
      [
        'sScoreText',
        'sVolumeText',
        'svScoreText',
        'sDispersionText',
        'sScoreText_',
        'sVolumeText_',
        'svScoreText_',
        'sDispersionText_',
      ].includes(colId)
    ) {
      if (value?.word) {
        const htmlDoc = parser.parseFromString(value.word, 'text/html');
        return htmlDoc.body.firstChild.textContent;
      }
      return '';
    } else if (colId === 'wordCountChange') {
      return currencyFormater(value, 0, '');
    } else if (colId === 'wordCountChangePercent') {
      return value ? `${value}%` : '';
    }
    return value;
  }
};

export const parseNumber = number => {
  number = parseFloat(number);
  return isNaN(number) ? null : round(number, 2);
};

export const currencyFormater = (value, fractionDigits = 2, currencyType = 'USD', prefix = '', postfix = '') => {
  if (!value && value !== 0) {
    return null;
  }
  let number = Number(value);
  let formatedNumber = '';
  if (currencyType === '') {
    formatedNumber = number.toLocaleString('en-US', {
      minimumFractionDigits: fractionDigits,
    });
  } else {
    formatedNumber = number.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyType,
      minimumFractionDigits: fractionDigits,
    });
  }

  if (fractionDigits === 0) {
    if (formatedNumber.indexOf('.') !== -1) {
      formatedNumber = formatedNumber.split('.')[0];
    }
  }
  return `${prefix}${formatedNumber}${postfix}`;
};

export const filterDateComparator = (filterLocalDateAtMidnight, cellValue) => {
  if (!cellValue) return 0;
  const cellDate = moment(cellValue, getUserDateFormat()).toDate();
  if (cellDate < filterLocalDateAtMidnight) return -1;
  else if (cellDate > filterLocalDateAtMidnight) return 1;
  else return 0;
};

export const sortingDateComparator = (date1, date2) => {
  try {
    const format = `${getUserDateFormat()} HH:mm:ss`;
    if (!date1 && !date2) return 0;
    if (!date1) return -1;
    if (!date2) return 1;
    const date1Number = moment(date1, format).valueOf();
    const date2Number = moment(date2, format).valueOf();
    return date1Number - date2Number;
  } catch (e) {
    return 0;
  }
};

export const getValueByJsonType = (jsonType, sentiment, wordCount) => {
  // If jsonType is 'non-scored', both sentimentValue and wordCountValue must be null to show pending in the cell.
  // If jsonType is 'scored' or empty, sentimentValue and wordCountValue must be rounded values or undefined to show the value or a blank space in the cell respectively.
  let sentimentValue = null;
  let wordCountValue = null;
  if (jsonType === 'scored' || jsonType === '') {
    sentimentValue = sentiment !== null ? round(sentiment, 2) : undefined;
    wordCountValue = wordCount !== null ? round(wordCount, 2) : undefined;
  }

  return { sentimentValue, wordCountValue };
};

export const pendingCellRenderer = params => (params.value === null ? '<i>pending</i>' : params.value);

export const sortingNumericComparator = (firstValue, secondValue, nodeA, nodeB, isSortInverted) => {
  //this comparator will sort numeric value and place all the null/undefined values at the bottom, regardless of the sort order.
  if (!firstValue && firstValue !== 0) {
    return isSortInverted ? -1 : 1;
  }
  if (!secondValue && secondValue !== 0) {
    return isSortInverted ? 1 : -1;
  }
  return firstValue - secondValue;
};

export const watchlistFrameworkComponents = {
  WordStatusRenderer: WordStatus,
  AggregatedSentimentRenderer,
  SentimentChangeRenderer,
  WordCountChangeRenderer,
  WordCountChangePercentageRenderer,
  AddRemoveIcon: AddRemoveIcon,
  TickerLogo: TickerLogo,
  CountryCodeRenderer: CountryCodeRenderer,
  TweetIcon: TweetsIcon,
  ScoreWordRenderer,
  SocialCheckboxRenderer,
};

export const headerHeightSetter = params => {
  const columnHeaderTexts = [...document.querySelectorAll('.ag-header-cell-text')];
  const clientHeights = columnHeaderTexts.map(headerText => headerText.clientHeight);
  const tallestHeaderTextHeight = Math.max(...clientHeights);
  const padding = 25;
  params.api.setHeaderHeight(tallestHeaderTextHeight + padding);
  params.api.resetRowHeights();
};

export const handleColumnsState = (params, keyName) => {
  if (['sortChanged', 'columnResized', 'columnMoved', 'columnVisible'].includes(params.type)) {
    storeColumnsState(keyName, params.columnApi.getColumnState());
    if (['columnResized', 'columnVisible'].includes(params.type)) headerHeightSetter(params);
  }
};

export const handleGridReady = (params, columnsKey, filtersKey) => {
  WatchlistService.init(params.api, params.columnApi); // global service
  const columnsState = getColumnState(columnsKey);
  if (columnsState?.length) {
    params.columnApi.applyColumnState({ state: columnsState, applyOrder: true });
  }
  const filteringState = getFilteringState(filtersKey);
  if (filteringState && !isEmpty(filteringState)) {
    params.api.setFilterModel(filteringState);
  }
  headerHeightSetter(params);
  setTimeout(() => {
    const inputFilters = document.getElementsByClassName('ag-text-field-input');
    forEach(inputFilters, data => data.setAttribute('autocomplete', 'off'));
  }, 1000);
};

export const onGridSizeChanged = debounce(params => {
  if (params.clientWidth < 800) {
    // for tablet and below tablet screens
    const columnsForRemovePinInMobileView = params.columnApi.getDisplayedLeftColumns();
    if (columnsForRemovePinInMobileView?.length) {
      params.columnApi.setColumnsPinned(columnsForRemovePinInMobileView, false);
    }
  }
}, 300);

export const getFilteringPayload = () => {
  const filterColumnsFromBackend = [
    'ticker',
    'company_name',
    'country',
    'document_type',
    'source',
    'industry',
    'sector',
  ];

  const filteringModel = WatchlistService.agGridAPI?.getFilterModel() ?? {};
  let filtersObjectForApiCall = {};
  filterColumnsFromBackend.forEach(key => {
    if (filteringModel[key]) {
      const filterText = filteringModel[key].filter.toLowerCase();
      // if user try to filter 'Earnings Call' document type then change it to 'FMP-Transcript' for back-end
      if (key === 'document_type' && filterText.length > 2 && 'earnings call'.includes(filterText)) {
        filtersObjectForApiCall[`filter_${key}`] = '*fmp-transcript*';
      } else {
        filtersObjectForApiCall[`filter_${key}`] = `*${filteringModel[key].filter}*`;
      }
    }
  });
  if (filteringModel['country']) {
    const countryies = getFilterCountriesArray(filteringModel.country.filter);
    if (countryies) {
      filtersObjectForApiCall['filter_country'] = countryies;
    } else {
      filtersObjectForApiCall = {};
    }
  }
  return filtersObjectForApiCall;
};
